/**
 * CLE-2811 — the contract-model list's Hospital and Created-by filters. A super admin's list spans every hospital
 * (`GET /runs`, newest 500 — the page says so when the API left older ones out), so she narrows it here, client-side,
 * over the rows already loaded. Pure helpers — the page only wires them to two dropdowns — plus the per-browser memory
 * of the two choices.
 *
 * Two states, kept apart: the SAVED choices (what she picked, remembered as-is) and the EFFECTIVE filters (what is
 * applied: derived from the saved choices and the options on offer, every render). A change writes only the field she
 * changed into the saved choices, so a remembered choice that is merely not on offer right now is never overwritten.
 */
import { IDraftRunSummary } from "store/queries/contractModelDrafts";

import {
  ALL_HOSPITALS_LABEL,
  EVERYONE_LABEL,
  LOADING_CREATOR,
  MINE_LABEL,
  NO_VALUE,
  YOU_LABEL,
  YOU_SUFFIX,
  unknownCreatorLabel,
} from "./copy";

/** Hospital filter: every hospital. Any other value is an `accountName`. */
export const ALL_HOSPITALS = "__all_hospitals__";
/** Created-by filter: every creator. Any other value except MINE is a `createdById`. */
export const EVERYONE = "__everyone__";
/** Created-by filter: the signed-in user's runs. */
export const MINE = "__mine__";

export interface IRunListFilters {
  hospital: string;
  creator: string;
}

export const DEFAULT_RUN_LIST_FILTERS: IRunListFilters = {
  hospital: ALL_HOSPITALS,
  creator: EVERYONE,
};

export interface IRunFilterOption {
  label: string;
  value: string;
}

type RunRow = Pick<
  IDraftRunSummary,
  "accountName" | "createdById" | "createdByName"
>;

/**
 * The Created-by cell: the creator's name, " (you)" after it when it is the signed-in user; when the API could not name
 * the creator, "You" for the signed-in user's own run and "—" for anyone else's.
 */
export const creatorLabel = (
  run: Pick<IDraftRunSummary, "createdById" | "createdByName">,
  currentUserId: string | undefined
): string => {
  const isMe = !!currentUserId && run.createdById === currentUserId;
  if (!run.createdByName) return isMe ? YOU_LABEL : NO_VALUE;
  return isMe ? `${run.createdByName}${YOU_SUFFIX}` : run.createdByName;
};

/** "All hospitals", then every hospital named on a loaded row, alphabetically. */
export const hospitalOptions = (runs: RunRow[]): IRunFilterOption[] => {
  const names = Array.from(
    new Set(
      runs.map((r) => r.accountName).filter((name): name is string => !!name)
    )
  ).sort((a, b) => a.localeCompare(b));
  return [
    { label: ALL_HOSPITALS_LABEL, value: ALL_HOSPITALS },
    ...names.map((name) => ({ label: name, value: name })),
  ];
};

/**
 * "Everyone", "Mine", then every OTHER creator on a loaded row (by id), alphabetically by label. The signed-in user is
 * "Mine" and is not listed a second time under her own name. A creator the API could not name reads
 * "Unknown user (<first 8 of the id>)", so two of them stay two distinguishable entries.
 */
export const creatorOptions = (
  runs: RunRow[],
  currentUserId: string | undefined
): IRunFilterOption[] => {
  const byId = new Map<string, string>();
  runs.forEach((r) => {
    if (byId.has(r.createdById)) return;
    if (currentUserId && r.createdById === currentUserId) return;
    byId.set(
      r.createdById,
      r.createdByName || unknownCreatorLabel(r.createdById)
    );
  });
  const creators = Array.from(byId, ([value, label]) => ({ label, value }));
  creators.sort((a, b) => a.label.localeCompare(b.label));
  return [
    { label: EVERYONE_LABEL, value: EVERYONE },
    { label: MINE_LABEL, value: MINE },
    ...creators,
  ];
};

/**
 * The options before the list has loaded once: the defaults plus the remembered choice itself, so the (disabled)
 * dropdowns show her choice as it is — no fallback computed yet — instead of falling back and then jumping when the
 * rows arrive. A remembered hospital is its own label (the value is the name); a remembered creator's name is not
 * known until the rows arrive.
 */
export const pendingRunFilterOptions = (
  saved: IRunListFilters
): { hospitals: IRunFilterOption[]; creators: IRunFilterOption[] } => {
  const hospitals = hospitalOptions([]);
  const creators = creatorOptions([], undefined);
  if (saved.hospital !== ALL_HOSPITALS)
    hospitals.push({ label: saved.hospital, value: saved.hospital });
  if (saved.creator !== EVERYONE && saved.creator !== MINE)
    creators.push({ label: LOADING_CREATOR, value: saved.creator });
  return { hospitals, creators };
};

/**
 * The filters as applied, from the saved choices: a saved choice that no option offers any more (that hospital's or
 * that creator's runs are gone, or hidden by the canceled-and-failed switch) falls back to its default, so a dropdown
 * never shows a blank value while it silently hides rows. The saved choice itself is not overwritten — the page keeps
 * it apart and writes only a field she changes.
 */
export const effectiveRunListFilters = (
  filters: IRunListFilters,
  hospitals: IRunFilterOption[],
  creators: IRunFilterOption[]
): IRunListFilters => ({
  hospital: hospitals.some((o) => o.value === filters.hospital)
    ? filters.hospital
    : ALL_HOSPITALS,
  creator: creators.some((o) => o.value === filters.creator)
    ? filters.creator
    : EVERYONE,
});

/** The rows both filters keep. "Mine" keeps nothing while the signed-in user is not known. */
export const filterRuns = <T extends RunRow>(
  runs: T[],
  filters: IRunListFilters,
  currentUserId: string | undefined
): T[] =>
  runs.filter((r) => {
    if (
      filters.hospital !== ALL_HOSPITALS &&
      r.accountName !== filters.hospital
    )
      return false;
    if (filters.creator === EVERYONE) return true;
    if (filters.creator === MINE)
      return !!currentUserId && r.createdById === currentUserId;
    return r.createdById === filters.creator;
  });

// ---------------------------------------------------------------------------------------------
// The two choices, remembered per browser
// ---------------------------------------------------------------------------------------------

export const RUN_LIST_FILTERS_STORAGE_KEY =
  "clearhealth.contractModels.listFilters";

/** `localStorage`, or null where touching it throws (a sandboxed frame, storage disabled). */
const browserStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** Her last choices; the defaults when she has never chosen, or when the browser will not say. */
export const readRunListFilters = (
  storage: Pick<Storage, "getItem"> | null = browserStorage()
): IRunListFilters => {
  try {
    const raw = storage?.getItem(RUN_LIST_FILTERS_STORAGE_KEY);
    if (!raw) return DEFAULT_RUN_LIST_FILTERS;
    const parsed = JSON.parse(raw) as Partial<IRunListFilters> | null;
    return {
      hospital:
        typeof parsed?.hospital === "string" ? parsed.hospital : ALL_HOSPITALS,
      creator: typeof parsed?.creator === "string" ? parsed.creator : EVERYONE,
    };
  } catch {
    return DEFAULT_RUN_LIST_FILTERS;
  }
};

/** Remember her choices. A browser that refuses (private mode, quota) just does not remember them. */
export const writeRunListFilters = (
  filters: IRunListFilters,
  storage: Pick<Storage, "setItem"> | null = browserStorage()
): void => {
  try {
    storage?.setItem(RUN_LIST_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Not remembered — the filters still apply on this page.
  }
};
